import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  getInitialIntroStepFormState,
  getMonthlyRentalOptions,
} from "../rentalUtils";

import { StyledAlert } from "App/global-style";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert";
import { PostNumberAndCity } from "Components/Form/PostNumberAndCity";
import { Select } from "Components/Form/Select";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getError } from "Utils/productUtils";
import React from "react";
import ReactMarkdown from "react-markdown";
import { RENTAL_INTRO_STEP } from "..";
import { RentalProductIntroFormValues } from "../rentalFormValuesInterfaces";
import { validationSchema } from "./rentalProductIntroValidation";

export const RentalProductIntro = ({
  updatePriceRequest,
}: ProductProps): React.ReactElement => {
  const dispatch = useDispatch();

  const initialFormState = useFormState<RentalProductIntroFormValues>(
    RENTAL_INTRO_STEP.id,
    getInitialIntroStepFormState(),
  );

  const { getText } = useTexts();
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const handleSubmit = (formValues: RentalProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: RENTAL_INTRO_STEP.id,
        formState: formValues,
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.RENTAL.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        monthlyRent: Number(formValues.monthlyRent),
        zipCode: formValues.postNumber,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.RENTAL}`,
        );
      }}
    >
      <ProductIntroTile
        header={RENTAL_INTRO_STEP.title}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        productBackendName={ProductBackendName.RENTAL}
      >
        {(formProps: FormikProps<RentalProductIntroFormValues>) => {
          return (
            <Form>
              <PartnerProductIntroForms />
              <PostNumberAndCity
                formProps={formProps}
                label={getText("rentalform.label.postnumber")}
              />
              <Select
                id="monthlyRent"
                name="monthlyRent"
                label={getText("rentalform.label.monthlyrent")}
                hint={getText("rentalform.hint.monthlyrent")}
                options={getMonthlyRentalOptions()}
                error={getError("monthlyRent", formProps)}
                large
              />

              <StyledAlert
                type="info"
                title={getText("rentalform.alerttitle.conditions")}
              >
                <ReactMarkdown>
                  {getText("rentalform.alertdescription.conditions")}
                </ReactMarkdown>
              </StyledAlert>

              <BackendErrorAlert priceCalculation={priceCalculationResult} />

              <ScrollToFirstError />
              <NavigationButtons
                isLoading={priceCalculationResult.isLoading}
                hidePreviousButton
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.RENTAL}
                stepId={RENTAL_INTRO_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
